import { useContext } from 'react'

import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCustomItem, { useCustomItemForm } from '@hooks/useCustomItem'

import { confirmDelete } from '@functions/entity'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditCustomItemModal'

type ModalCallbacks = {
  closeModal: () => void,
  createCustomItem: (payload: HandleCallbackActionParams) => void,
  updateCustomItem: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCustomItemModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createCustomItem, updateCustomItem } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const customItem = digObject(modalPayload, 'customItem', {})

  const newRecord = !customItem.id

  const {
    callbacks: {
      createCustomItem: createFn,
      deleteCustomItem: deleteFn,
      updateCustomItem: updateFn,
    },
    creating,
    deleting,
    updating,
  } = useCustomItem(customItem)

  const {
    entityState,
    entityState: {
      description,
      price,
      title,
    },
    errors,
    handlers,
    saveEnabled,
  } = useCustomItemForm(customItem, { validateOn: 'change' })

  const createOrUpdateCustomItemPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Custom Item ${newRecord ? 'Created' : 'Updated'}`,
  }

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    strings: {
      deleteString: 'remove',
      entityName: 'Custom Item',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Custom Item" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Price" errorMessage={errors.price} marginTop="large">
            <input name="price" type="number" value={price} {...handlers} />
          </Form.Field>

          <Form.Field label="Description" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea
              name="description"
              value={description}
              {...handlers}
              style={{ height: 120, resize: 'vertical' }}
            />
          </Form.Field>
        </Form>

        {!newRecord && (
          <Form.Field label="More Options" marginTop="large">
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faTrashAlt} />}
              loading={deleting}
              // @ts-expect-error TS2345
              onClick={() => confirmDelete(confirmDeletePayload)}
              size="medium"
            >
              Delete Item
            </Button>
          </Form.Field>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createCustomItem(createOrUpdateCustomItemPayload)
              : () => updateCustomItem(createOrUpdateCustomItemPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Custom Item` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCustomItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
